import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthRole, type StaffRole } from "@/hooks/useAuthRole";
import { supabase } from "@/lib/supabase";
import { AdminSessionContext } from "./AdminSessionContext";

interface AdminGuardProps {
  children: ReactNode;
  requireAdmin?: boolean;
  allow?: StaffRole[];
}

function CenteredPanel({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background-100 px-4">
      <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-8 text-center">
        {children}
      </div>
    </div>
  );
}

export default function AdminGuard({ children, requireAdmin = false, allow }: AdminGuardProps) {
  const auth = useAuthRole();
  const { session, role, username, loading } = auth;
  const navigate = useNavigate();
  const [bootstrapping, setBootstrapping] = useState(false);
  const [bootstrapError, setBootstrapError] = useState("");

  useEffect(() => {
    if (!loading && !session) {
      navigate("/admin/login", { replace: true });
    }
  }, [loading, session, navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin/login", { replace: true });
  };

  const handleBootstrap = async () => {
    setBootstrapping(true);
    setBootstrapError("");
    try {
      const { data, error } = await supabase.functions.invoke("admin-staff", {
        body: { action: "bootstrap" },
      });
      if (error) {
        const ctx = (error as { context?: Response }).context;
        let message = "初始化失败，请稍后重试";
        if (ctx && typeof ctx.json === "function") {
          try {
            const parsed = await ctx.json();
            if (parsed?.error) message = String(parsed.error);
          } catch {
            /* keep default message */
          }
        }
        throw new Error(message);
      }
      if (data?.error) throw new Error(String(data.error));
      await auth.reload();
    } catch (err) {
      setBootstrapError(err instanceof Error ? err.message : "初始化失败，请稍后重试");
    } finally {
      setBootstrapping(false);
    }
  };

  if (loading || !session) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100">
        <div className="flex flex-col items-center gap-3 text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
          <span className="text-sm">正在验证登录状态…</span>
        </div>
      </div>
    );
  }

  if (!role) {
    return (
      <CenteredPanel>
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-accent-600">
          <i className="ri-user-settings-line text-2xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-lg font-semibold text-foreground-950">
          账号尚未分配权限
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-foreground-500">
          当前登录账号 <span className="text-foreground-800">{username || "该账号"}</span>{" "}
          还没有被分配角色。若你是系统首位使用者，可直接初始化成为管理员；否则请联系管理员开通。
        </p>
        {bootstrapError && (
          <p className="mt-3 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-xs text-accent-700">
            {bootstrapError}
          </p>
        )}
        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={handleBootstrap}
            disabled={bootstrapping}
            className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {bootstrapping ? (
              <i className="ri-loader-4-line animate-spin"></i>
            ) : (
              <i className="ri-shield-star-line"></i>
            )}
            初始化成为管理员
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
          >
            <i className="ri-logout-box-r-line"></i>
            退出登录
          </button>
        </div>
      </CenteredPanel>
    );
  }

  if (allow ? !allow.includes(role) : requireAdmin && role !== "admin") {
    return (
      <CenteredPanel>
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
          <i className="ri-lock-2-line text-2xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-lg font-semibold text-foreground-950">
          无访问权限
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          {requireAdmin ? "该模块仅限管理员访问。" : "当前登录角色无权访问该模块。"}
        </p>
        <button
          type="button"
          onClick={() => navigate("/admin", { replace: true })}
          className="mt-6 inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line"></i>
          返回会员列表
        </button>
      </CenteredPanel>
    );
  }

  return (
    <AdminSessionContext.Provider value={auth}>{children}</AdminSessionContext.Provider>
  );
}